import { useEffect, useState } from "react";

import HollowButton from "components/HollowButton";
import Loading from "components/Loading";
import PDFViewer from "components/PDFViewer";

import localLawService from "api/localLawService";
import { ILegalActListItem } from "dto/LocalLaw/ILocalLaw";

import styles from "./styles.module.scss";

interface Props {
  act: ILegalActListItem;
  onClose: () => void;
}

// Podgląd aktu prawa miejscowego.
//
// PDF składa backend: treść aktu przeliczoną z docx plus dołączone załączniki —
// odpowiednik akt.pdf z Magistratu. Front nie skleja tu niczego samodzielnie,
// bo kolejność i zawartość podglądu mają odpowiadać temu, co pójdzie do publikacji.
//
// Podgląd powstaje na żądanie, więc zawsze pokazuje bieżącą treść dokumentu —
// także zmiany zapisane przed chwilą z Worda.

const errorText = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { Message?: string } } })?.response?.data?.Message ??
  fallback;

const actHeading = (act: ILegalActListItem) =>
  act.ActNumber ? `${act.ActTypeName} Nr ${act.ActNumber}` : act.ActTypeName;

const PreviewModal = ({ act, onClose }: Props) => {
  const [blob, setBlob] = useState<Blob | null>(null);
  const [skipped, setSkipped] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    localLawService
      .getPreview(act.Id)
      .then((preview) => {
        if (cancelled) return;

        setBlob(preview.Blob);
        setSkipped(preview.Skipped);
      })
      .catch((e) => {
        if (!cancelled) setError(errorText(e, "Nie udało się złożyć podglądu aktu."));
      });

    return () => {
      cancelled = true;
    };
  }, [act.Id]);

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div
        className={`${styles.modal} ${styles.modalPreview}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={styles.modalTitle}>
          Podgląd — {actHeading(act)}
          {act.Title ? ` — ${act.Title}` : ""}
        </div>

        {!!skipped.length && (
          <div className={styles.previewNotice}>
            Do podglądu nie dołączono załączników, których formatu nie da się pokazać na
            stronie: {skipped.join(", ")}. W paczce ZIPX są w całości.
          </div>
        )}

        <div className={styles.previewBody}>
          {error && <div className={styles.emptyList}>{error}</div>}

          {!error && !blob && <Loading />}

          {!error && blob && (
            <PDFViewer file={blob} title={actHeading(act)} withoutBorder />
          )}
        </div>

        <div className={styles.modalFooter}>
          <HollowButton text="Zamknij" onClick={onClose} />
        </div>
      </div>
    </div>
  );
};

export default PreviewModal;
